import { SubscriptionType } from "@common/types/billing";
import { parseISO } from "date-fns";
import { useBillingStatus } from "./useBillingStatus";
import { useCanManageWorkspaceBilling } from "./permissions/useCanManageWorkspaceBilling";
import { useActiveWorkspace } from "./useActiveWorkspace";

interface IUseBillingManagement {
    canManageBilling: boolean;
    bookkeepingStartDate: Date | null;
    bookkeepingEndDate: Date | null;

    showUsageLimitReachedCta: boolean; // no future transactions visible, because of expense limit reached
    showPastTransactionsCta: boolean; // no past transactions visible, because of previous year bookkeeping not included
    currentUsageLimit: number;
}

export function useBillingManagement(): IUseBillingManagement {
    const { isTrialing, plans, isFetched, subscriptionType } =
        useBillingStatus();

    const canManageBilling = useCanManageWorkspaceBilling();
    const activeWorkspace = useActiveWorkspace();

    if (!isFetched || !activeWorkspace) {
        return {
            canManageBilling: false,
            bookkeepingStartDate: null,
            bookkeepingEndDate: null,
            showUsageLimitReachedCta: false,
            showPastTransactionsCta: false,
            currentUsageLimit: 0,
        };
    }

    const [freePlan] = plans;
    const hasLimitedAccess = !!activeWorkspace.bookkeepingEndDate;

    const showUsageLimitReachedCta = hasLimitedAccess;

    const showPastTransactionsCta =
        subscriptionType === SubscriptionType.FLAT_RATE &&
        (isTrialing || hasLimitedAccess);

    return {
        canManageBilling,
        showUsageLimitReachedCta,
        showPastTransactionsCta,

        currentUsageLimit: freePlan?.usageLimit ?? 0,
        bookkeepingEndDate: activeWorkspace.bookkeepingEndDate
            ? parseISO(activeWorkspace.bookkeepingEndDate)
            : null,
        bookkeepingStartDate: parseISO(activeWorkspace.bookkeepingStartDate),
    };
}
